<template>
  <side-card
    :title="$t('components.FilterCardPagination.title')"
    :absolute="true"
    :loading="false"
    :width="!$vuetify.breakpoint.smAndDown ? VuetifyBreakpointEnum.SM : '100%'"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    @close="onRequestClose"
  >
    <!-- Scrollable filter list -->
    <div class="scrollable">
      <!-- APPLIED FILTER LIST -->
      <div
        class="v-item-group theme--light v-expansion-panels v-expansion-panels--accordion v-expansion-panels--flat pa-0"
      >
        <div class="v-expansion-panel ma-0 pa-0">
          <!-- SUGGESTIONS-->
          <div v-if="predefinedFilter && predefinedFilter.length">
            <div class="v-expansion-panel-header pa-0 text-subtitle-1">
              {{ $t("components.FilterCardPagination.suggestions") }}
              <span class="ml-1">
                <a-eicon :noColor="true" />
              </span>
            </div>
            <div class="v-expansion-panel-content pa-0">
              <div class="v-expansion-panel-content pa-0">
                <div class="v-expansion-panel-content__wrap pa-0">
                  <v-chip
                    v-for="(item, index) in predefinedAndQuickFilters"
                    :key="'applied-filter-' + index"
                    :color="isPredefinedFilterActive(item.name, item.isQuick) ? 'primary' : ''"
                    class="mr-1 mt-1"
                    label
                    @click="setPreselectedFilter(item.name, item.isQuick)"
                    :close="isPredefinedFilterActive(item.name, item.isQuick)"
                    @click:close="removePreselectedFilter(item.name, item.isQuick)"
                  >
                    {{ $t(item.name) }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
          <div class="v-expansion-panel-header pa-0 text-subtitle-1">
            {{ $t("components.FilterCardPagination.activeFilters") }}
          </div>
          <div class="v-expansion-panel-content pa-0">
            <div class="v-expansion-panel-content__wrap pa-0">
              <v-chip
                v-for="(item, index) in activeFiltersFromCompleteFilterListUniqueIds"
                :key="'applied-filter-' + index + item"
                class="ma-1"
                label
                color="info"
                @click="openFilterPanel(item)"
                close
                @click:close="clearItemValueById(item)"
              >
                {{ getDisplayName(item) }}
              </v-chip>
              <div v-if="!activeFiltersFromCompleteFilterList.length" class="mt-2 mb-1">
                <i>{{ $t("components.FilterCardPagination.noFilter") }}</i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-divider class="mt-3" />

      <!-- FILTER OPTIONS -->
      <v-expansion-panels v-model="panel" multiple accordion flat class="pa-0">
        <v-expansion-panel
          v-for="(item, i) in groupedFiltersList"
          :key="`v-expansion-panel-filter-card-pagination-option-${i}`"
          class="ma-0 pa-0"
          :id="getPanelId(item.filterOption.id)"
        >
          <v-expansion-panel-header class="pa-0" @click="onenFilterPanel(i)">
            {{ item.filterOption.displayName ? $t(item.filterOption.displayName) : item.filterOption.key }}
            <debug>
              <span v-for="(filter, index) in item.filter" :key="'filter-' + index + filter.key">
                <v-chip
                  v-if="filter.value !== ''"
                  small
                  class="mr-1"
                  close
                  label
                  @click:close="removeFilter(item, index)"
                >
                  {{ $t(`components.filter.${filter.operation}`) }}
                  {{ filter.value }}
                </v-chip>
              </span>
            </debug>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pa-0">
            <filter-card-pagination-side-card-filter-element class="mt-1" :value="item" />
          </v-expansion-panel-content>
          <v-divider v-if="i < groupedFiltersList.length - 1" />
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    <!-- Actions always at the bottom -->
    <v-card flat class="footer pa-0 ma-0">
      <v-card-actions>
        <v-spacer />
        <v-btn @click="removeAllAndClose" small outlined>
          {{ $t(`components.FilterCardPagination.removeAll`) }}
        </v-btn>
        <v-btn @click="confirmFilterAndClose" small elevation="0" color="info" data-test-filter-apply>
          {{ $t("confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <confirm-action-dialog
      :isDialogActive.sync="showConfirmDialog"
      max-width="400"
      :title="$t('components.FilterCardPagination.confirmDialogTitle')"
      :subtitle="$t('components.FilterCardPagination.confirmDialogSubtitle')"
      :confirm-label="$t('apply')"
      :cancel-label="$t('close')"
      @rightClick="onDialogConfirm"
      @leftClick="onDialogClose"
      @close="showConfirmDialog = false"
    />
  </side-card>
</template>

<script lang="ts">
import ConfirmActionDialog from "@/components/utility/ConfirmActionDialog.vue";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { PageFilterTypes } from "@/lib/utility/data/page-filter-types.enum";
import { isMobile } from "@/lib/utility/isMobile";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import { PaginationFilterListElement, PaginationFilterOperationEnum } from "@/store/modules/base-pagination.store";
import { PredefinedFilterType } from "@/views/event/filter/event.filter";
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import AEicon from "../utility/AEicon.vue";
import Debug from "../utility/Debug.vue";
import SideCard from "../utility/SideCard.vue";
import FilterCardPaginationMenuFilterElement from "./FilterCardPaginationMenuFilterElement.vue";
import FilterCardPaginationSideCardFilterElement from "./FilterCardPaginationSideCardFilterElement.vue";
import { VuetifyBreakpointEnum } from "@/lib/enum/vuetify-breakpoint.enum";

export interface IGroupedFilters {
  filterOption: PaginationFilterListElement;
  filter: IPageFilterElement[];
}

interface ExtendedPredefinedFilterType extends PredefinedFilterType {
  isQuick: boolean;
}

@Component({
  components: {
    FilterCardPaginationMenuFilterElement,
    FilterCardPaginationSideCardFilterElement,
    SideCard,
    ConfirmActionDialog,
    Debug,
    AEicon
  }
})
export default class FilterCardPaginationSideCard extends Vue {
  readonly VuetifyBreakpointEnum = VuetifyBreakpointEnum;

  showConfirmDialog = false;

  panel: number[] = [];

  @Prop({ default: () => [] })
  quickFilters!: PredefinedFilterType[];

  /**
   * ALL AVAILABLE FILTER OPTIONS
   */
  @Prop({ default: [] })
  filterList!: PaginationFilterListElement[];

  /**
   * ALL FILTERS THAT ARE CURRENTLY APPLIED
   */
  @Prop({ default: () => [] })
  filter!: IPageFilterElement[];

  /**
   * OPTIONS FOR GROUPS OF PREFILLED FILTERS
   */
  @Prop({ default: undefined })
  predefinedFilter!: PredefinedFilterType[];

  /**
   * SEARCH TEXT
   */
  @Prop()
  search!: string;

  groupedFiltersList: IGroupedFilters[] = [];

  /**
   * Mush the predefined and quick filters together, but remove duplicates (by name or by filter content) and flag them as quick or not
   */
  get predefinedAndQuickFilters(): ExtendedPredefinedFilterType[] {
    const all: ExtendedPredefinedFilterType[] = [];
    if (this.predefinedFilter) {
      for (const pf of this.predefinedFilter) {
        const nameAlreadyExists = all.find((f) => f.name === pf.name);
        if (nameAlreadyExists) {
          continue;
        }
        const filterAlreadyExists = all.find((a) =>
          a.filter.every(
            (af) =>
              a.filter.length === pf.filter.length &&
              pf.filter.some((f) => f.key === af.key && f.operation === af.operation && f.value === af.value)
          )
        );
        if (filterAlreadyExists) {
          continue;
        }
        all.push({ ...pf, isQuick: false });
      }
    }

    if (this.quickFilters) {
      for (const qf of this.quickFilters) {
        const nameAlreadyExists = all.find((f) => f.name === qf.name);
        if (nameAlreadyExists) {
          continue;
        }
        const filterAlreadyExists = all.find((a) =>
          a.filter.every(
            (af) =>
              a.filter.length === qf.filter.length &&
              qf.filter.some((f) => f.key === af.key && f.operation === af.operation && f.value === af.value)
          )
        );
        if (filterAlreadyExists) {
          continue;
        }
        all.push({ ...qf, isQuick: true });
      }
    }
    return all;
  }

  get isMobile() {
    return isMobile();
  }

  get operations() {
    return Object.values(PaginationFilterOperationEnum);
  }

  get activeFiltersFromCompleteFilterList() {
    return this.groupedFiltersList
      .map((f) => f.filter)
      .flat()
      .filter((f) => f.value !== "");
  }

  get activeFiltersFromCompleteFilterListUniqueIds() {
    return Array.from(new Set(this.activeFiltersFromCompleteFilterList.map((f) => f.key)));
  }

  get filtersChanged() {
    const newFilters = this.groupedFiltersList
      .map((f) => f.filter)
      .flat()
      .filter((f) => f.value !== "");
    const currentFilters = this.filter;

    if (newFilters.length !== currentFilters.length) {
      return true;
    }

    for (const newFilter of newFilters) {
      const matchingCurrentFilter = currentFilters.find(
        (f) => f.key === newFilter.key && f.operation === newFilter.operation && f.value === newFilter.value
      );
      if (!matchingCurrentFilter) {
        return true;
      }
    }

    for (const currentFilter of currentFilters) {
      const matchingNewFilter = newFilters.find(
        (f) => f.key === currentFilter.key && f.operation === currentFilter.operation && f.value === currentFilter.value
      );
      if (!matchingNewFilter) {
        return true;
      }
    }

    return false;
  }

  onRequestClose() {
    if (this.filtersChanged) {
      this.showConfirmDialog = true;
    } else {
      this.close();
    }
  }

  onDialogClose() {
    this.showConfirmDialog = false;
    this.close();
  }

  onDialogConfirm() {
    this.showConfirmDialog = false;
    this.confirmFilterAndClose();
  }

  @Emit("update:filter")
  updateFilter(filter: IPageFilterElement[]) {
    return filter;
  }

  @Emit("close")
  close() {
    // Just to emit the event
  }

  mounted() {
    this.initCompleteFilterList();
    this.openSelectedPanels();
    window.addEventListener("keydown", this.handleCmdShortcuts, true);
  }

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleCmdShortcuts, true);
  }

  handleCmdShortcuts(e: KeyboardEvent) {
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const metaKey = isMac ? e.metaKey : e.ctrlKey;
    // Cmd+Enter (confirm)
    if (metaKey && (e.key === "Enter" || e.key === "NumpadEnter")) {
      e.preventDefault();
      this.confirmFilterAndClose();
    }
    // Cmd+Backspace or Cmd+Delete (remove all and close)
    if (metaKey && (e.key === "Backspace" || e.key === "Delete")) {
      e.preventDefault();
      this.removeAllAndClose();
    }
    // Cmd+Escape (close immediately, no dialog)
    if (metaKey && e.key === "Escape") {
      e.preventDefault();
      this.close();
    }
  }

  @Watch("groupedFiltersList", { immediate: true, deep: true })
  openSelectedPanels() {
    let index = 0;
    for (const filter of this.groupedFiltersList) {
      if (filter.filter.some((f) => f.value !== "")) {
        this.panel.push(index);
      }
      index++;
    }
  }

  @Emit("confirmFilter")
  confirmFilter() {
    this.filter.splice(0);
    this.filter.push(...this.activeFiltersFromCompleteFilterList);
    this.updateFilter(this.activeFiltersFromCompleteFilterList);
  }

  confirmFilterAndClose() {
    this.confirmFilter();
    this.close();
  }

  removeAll() {
    const groupedFilterList: IGroupedFilters[] = this.filterList.map((f) => ({
      filter: [],
      filterOption: f
    }));
    this.groupedFiltersList.splice(0);
    this.groupedFiltersList.push(...groupedFilterList);
  }

  removeAllAndClose() {
    this.removeAll();
    this.confirmFilter();
    this.close();
  }

  initCompleteFilterList() {
    this.groupedFiltersList.splice(0);
    const groupedFilterList = this.filterList.map((f) => {
      const found = this.filter.filter((cf) => cf.key === f.key);
      if (found.length) {
        return {
          filter: [...found.map((fv) => new PageFilterElement(fv))],
          filterOption: f
        };
      }
      return {
        filter: [],
        filterOption: f
      };
    });
    this.groupedFiltersList.push(...groupedFilterList);
  }

  getPanelId(id: string) {
    return `filter-panel-${id}`;
  }

  openFilterPanel(id: string) {
    const index = this.filterList.findIndex((f) => f.id === id);

    if (!this.panel.includes(index)) {
      this.panel.push(index);
    }

    document.getElementById(this.getPanelId(id))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  removeFilter(item: IGroupedFilters, index: number) {
    item.filter.splice(index, 1);
  }

  getDisplayName(id: string) {
    const found = this.filterList.find((f) => f.id === id);
    return found ? (found.displayName ? this.$t(found.displayName) : found.key) : id;
  }

  findPredefinedFilterByName(name: string, isQuickFilter: boolean) {
    return this.predefinedAndQuickFilters.find((f) => f.name === name && f.isQuick === isQuickFilter);
  }

  isPredefinedFilterActive(predefinedFilterName: string, isQuickFilter: boolean): boolean {
    const predefinedFilter = this.findPredefinedFilterByName(predefinedFilterName, isQuickFilter);
    if (!predefinedFilter) {
      return false;
    }

    return predefinedFilter.filter.every((predefined) =>
      this.activeFiltersFromCompleteFilterList.find(
        (af) => af.key === predefined.key && af.operation === predefined.operation && af.value === predefined.value
      )
    );
  }

  setFilters(filters: IPageFilterElement[]) {
    this.removeAll();

    for (const f of filters) {
      const groupedFilterList = this.groupedFiltersList.find((cfl) => cfl.filterOption.key === f.key);
      if (!groupedFilterList) {
        continue;
      }

      const filter = groupedFilterList.filter.find((f) => f.value === "");
      if (filter) {
        filter.value = f.value;
        filter.operation = f.operation;
      } else {
        if (
          groupedFilterList.filter.find(
            (fv) => fv.key === f.key && fv.value === f.value && fv.operation === f.operation
          )
        ) {
          continue;
        }
        groupedFilterList.filter.push(
          new PageFilterElement({
            key: f.key,
            operation: f.operation,
            value: f.value,
            type: f.type
          })
        );
      }
    }
  }

  setPreselectedFilter(predefinedFilterName: string, isQuickFilter: boolean): void {
    const predefinedFilterToAdd = this.findPredefinedFilterByName(predefinedFilterName, isQuickFilter);
    if (!predefinedFilterToAdd) {
      return;
    }

    for (const predefinedFilter of predefinedFilterToAdd.filter) {
      const found = this.groupedFiltersList.find((g) => g.filterOption.key === predefinedFilter.key);

      if (!found) {
        continue;
      }

      // A boolean field holds exactly one value, so an already active predicate on the same key
      // (e.g. the billing run's default `isBilled = false`) is not narrowed by the one this
      // suggestion applies - the backend `$and`s both and the result is always empty. Replace.
      if (found.filterOption.type === PageFilterTypes.BOOLEAN) {
        found.filter.splice(0);
      }

      if (
        found.filter.find(
          (f) =>
            f.key === predefinedFilter.key &&
            f.operation === predefinedFilter.operation &&
            f.value === predefinedFilter.value
        )
      ) {
        continue;
      }

      found.filter.push(
        new PageFilterElement({
          key: predefinedFilter.key,
          operation: predefinedFilter.operation,
          value: predefinedFilter.value,
          type: predefinedFilter.type
        })
      );
    }
  }

  removePreselectedFilter(predefinedFilterName: string, isQuickFilter: boolean): void {
    const predefinedFilterToRemove = this.findPredefinedFilterByName(predefinedFilterName, isQuickFilter);
    if (!predefinedFilterToRemove) {
      return;
    }

    for (const groupedFilter of this.groupedFiltersList) {
      for (const filterToRemove of predefinedFilterToRemove.filter) {
        if (groupedFilter.filterOption.key !== filterToRemove.key) {
          continue;
        }
        // We remove all filters from this grouped filter that match the target filter key, value and operation EVEN if they are still part of another active predefined filter -> otherwise there are situations where the filter would not be unselected (if it is a subset of another active predefined filter)
        const remainingFilters = groupedFilter.filter.filter(
          (f) =>
            !(
              f.key === filterToRemove.key &&
              f.operation === filterToRemove.operation &&
              f.value === filterToRemove.value
            )
        );
        groupedFilter.filter.splice(0);
        groupedFilter.filter.push(...remainingFilters);
      }
    }
  }

  clearItemValue(item: IPageFilterElement) {
    item.value = "";
    item.operation = PageFilterOperationEnum.EQUAL;
  }

  clearItemValueById(itemId: string) {
    const grouped = this.groupedFiltersList.find((gf) => gf.filterOption.id === itemId);
    grouped?.filter.splice(0);
  }

  get shortcutKeyLabel() {
    if (typeof navigator !== "undefined") {
      return navigator.platform.toUpperCase().indexOf("MAC") >= 0 ? "⌘" : "Ctrl";
    }
    return "Ctrl";
  }

  /**
   * Open filter panel and add empty filter if none exists
   * @param id
   */
  onenFilterPanel(id: number) {
    const isOpening = !this.panel.includes(id); // when it was closed and is now opening

    if (!isOpening) {
      return;
    }

    const filter = this.groupedFiltersList[id];
    if (!filter) {
      return;
    }

    if (filter.filter.length) {
      return;
    }

    filter.filter.push(
      new PageFilterElement({
        key: filter.filterOption.key,
        operation: PaginationFilterOperationEnum.EQUAL,
        value: "",
        type: filter.filterOption.type
      })
    );
  }
}
</script>

<style scoped>
.scrollable {
  overflow: visible;
  flex: 1 1 auto;
  min-height: 0;
  padding-bottom: 72px;
}

.footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  flex-shrink: 0;
  padding: 8px 16px;
}
</style>
